import axiosInstance from "../../components/api/axiosInstance";

const unwrap = (res) => res.data?.data;

/** Same API surface for staff (/work, all clients) and tenant portal (/client/work, own company). */
export function createWorkApi(base, { isStaff = false, linkBase = "" } = {}) {
  const get = (url, params) => axiosInstance.get(`${base}${url}`, { params }).then(unwrap);
  const post = (url, body) => axiosInstance.post(`${base}${url}`, body || {}).then(unwrap);
  const put = (url, body) => axiosInstance.put(`${base}${url}`, body || {}).then(unwrap);
  const patch = (url, body) => axiosInstance.patch(`${base}${url}`, body || {}).then(unwrap);
  const del = (url) => axiosInstance.delete(`${base}${url}`).then(unwrap);

  return {
    isStaff,
    /** Prefix for in-app links: "" for staff, "/portal" for tenant users. */
    linkBase,
    link: (path) => `${linkBase}/${String(path || "").replace(/^\//, "")}`,

    meta: (params) => get("/meta", params),
    access: () => get("/access"),
    areas: () => get("/areas"),
    projects: (params) => get("/projects", params),

    orgTree: () => get("/org/tree"),
    updateOrgMember: (userId, body) => patch(`/org/members/${userId}`, body),
    departments: () => get("/org/departments"),
    createDepartment: (body) => post("/org/departments", body),
    updateDepartment: (id, body) => patch(`/org/departments/${id}`, body),
    deleteDepartment: (id) => del(`/org/departments/${id}`),

    plan: (type, id) => get(`/projects/${type}/${id}/plan`),
    setOwner: (type, id, userId) => put(`/projects/${type}/${id}/owner`, { User_Id: userId }),
    createStream: (type, id, body) => post(`/projects/${type}/${id}/streams`, body),
    assign: (type, id, body) => post(`/projects/${type}/${id}/assign`, body),
    updateStream: (id, body) => patch(`/streams/${id}`, body),
    deleteStream: (id) => del(`/streams/${id}`),
    createItem: (streamId, body) => post(`/streams/${streamId}/items`, body),

    item: (id) => get(`/items/${id}`),
    updateItem: (id, body) => patch(`/items/${id}`, body),
    deleteItem: (id) => del(`/items/${id}`),
    review: (id, decision, note) => post(`/items/${id}/review`, { decision, note }),
    comment: (id, body) => post(`/items/${id}/comments`, { Body: body }),
    addPerson: (id, userId, role) => post(`/items/${id}/people`, { User_Id: userId, Role: role }),
    removePerson: (id, assignmentId) => del(`/items/${id}/people/${assignmentId}`),
    readyPack: (id) => get(`/items/${id}/ready-pack`),
    markPosted: (id, body) => post(`/items/${id}/posted`, body),

    listItems: (params) => get("/items", params),
    myWork: (params) => get("/my-work", params),
    teamWork: (params) => get("/team-work", params),
    reviewInbox: () => get("/review-inbox"),

    submitDaily: (body) => post("/daily-status", body),
    dailyStatus: (params) => get("/daily-status", params),
    missingUpdates: (params) => get("/daily-status/missing", params),

    calendar: (params) => get("/calendar", params),
    moveOnCalendar: (body) => post("/calendar/move", body),
    markCalendarPostPosted: (postId, body) => post(`/calendar/posts/${postId}/posted`, body),

    notifications: (params) => get("/notifications", params),
    markRead: (ids) => post("/notifications/read", ids ? { ids } : {}),

    connections: (params) => get("/social/connections", params),
    addConnection: (body) => post("/social/connections", body),
    testConnection: (body) => post("/social/connections/test", body),
    testMetaLeadForms: (id, body) => post(`/social/connections/${id}/lead-forms`, body || {}),
    leadSources: (params) => get("/leads/sources", params),
    deleteConnection: (id) => del(`/social/connections/${id}`),
    oauthStart: (provider, params) => get(`/social/oauth/${provider}/start`, params),
    checkConnection: (id) => post(`/social/connections/${id}/check`),
    checkConnections: (body) => post("/social/connections/check", body),
    tummaSetup: () => get("/social/tumma-setup"),
    metaPartnerAssets: () => get("/meta/partner-assets"),
    assignMetaPartnerAssets: (body) => post("/meta/partner-assets/assign", body),
    publish: (itemId, body) => post(`/items/${itemId}/publish`, body),
    publishJobs: (itemId) => get(`/items/${itemId}/publish-jobs`),
    retryJob: (jobId) => post(`/publish-jobs/${jobId}/retry`),
    cancelJob: (jobId) => post(`/publish-jobs/${jobId}/cancel`),

    comments: (params) => get("/social/comments", params),
    replyComment: (id, message) => post(`/social/comments/${id}/reply`, { message }),
    updateComment: (id, body) => patch(`/social/comments/${id}`, body),
    syncEngagement: (body) => post("/social/sync", body),
    itemEngagement: (itemId) => get(`/items/${itemId}/engagement`),

    adAccounts: (params) => get("/ads/accounts", params),
    updateAdAccount: (id, body) => patch(`/ads/accounts/${id}`, body),
    deleteAdAccount: (id) => del(`/ads/accounts/${id}`),
    syncAds: (body) => post("/ads/sync", body),
    platformCampaigns: (params) => get("/ads/campaigns", params),
    googleLeadKey: (params) => get("/ads/google-lead-key", params),
    createGoogleLeadKey: (body) => post("/ads/google-lead-key", body),

    waNumbers: (params) => get("/whatsapp/numbers", params),
    addWaNumber: (body) => post("/whatsapp/numbers", body),
    deleteWaNumber: (id) => del(`/whatsapp/numbers/${id}`),
    checkWaNumber: (id) => post(`/whatsapp/numbers/${id}/check`),
    testWaAlert: () => post("/whatsapp/alerts/test"),
    waTemplates: (params) => get("/whatsapp/templates", params),
    waConversations: (params) => get("/whatsapp/conversations", params),
    waConversation: (contactId) => get(`/whatsapp/conversations/${contactId}`),
    waReply: (contactId, body) => post(`/whatsapp/conversations/${contactId}/reply`, body),
    waContacts: (params) => get("/whatsapp/contacts", params),
    updateWaContact: (id, body) => patch(`/whatsapp/contacts/${id}`, body),
    importWaContacts: (body) => post("/whatsapp/contacts/import", body),
    waBroadcasts: (params) => get("/whatsapp/broadcasts", params),
    createWaBroadcast: (body) => post("/whatsapp/broadcasts", body),
    cancelWaBroadcast: (id) => post(`/whatsapp/broadcasts/${id}/cancel`),

    socialThreads: (params) => get("/social/messages", params),
    socialThread: (threadId) => get(`/social/messages/${threadId}`),
    replySocialMessage: (threadId, text) => post(`/social/messages/${threadId}/reply`, { text }),

    trackItem: (itemId, postedUrl) => post(`/items/${itemId}/track`, postedUrl ? { Posted_URL: postedUrl } : {}),
    trackPost: (postId, postedUrl) => post(`/calendar/posts/${postId}/track`, postedUrl ? { Posted_URL: postedUrl } : {}),
    postTracking: (postId) => get(`/calendar/posts/${postId}/track`),
  };
}

export const staffWorkApi = createWorkApi("/work", { isStaff: true, linkBase: "" });
export const portalWorkApi = createWorkApi("/client/work", { linkBase: "/portal" });

/** Work API matching a DM api object (staff / portal). */
export const workApiFor = (dmApi) => (dmApi?.isStaff ? staffWorkApi : portalWorkApi);

export function errorMessage(err, fallback = "Something went wrong") {
  return err?.response?.data?.message || err?.response?.data?.error || fallback;
}

/** Plain-language status labels and a tone (colour family) per status. */
export const STATUS_INFO = {
  "Not Started": { label: "Not started", tone: "grey" },
  "In Progress": { label: "Working on it", tone: "blue" },
  "In Review": { label: "Waiting for review", tone: "violet" },
  "Changes Needed": { label: "Changes needed", tone: "amber" },
  Approved: { label: "Approved", tone: "teal" },
  Scheduled: { label: "Scheduled to post", tone: "indigo" },
  Posted: { label: "Posted", tone: "green" },
  Done: { label: "Done", tone: "green" },
  Blocked: { label: "Stuck", tone: "red" },
};

export const statusLabel = (s) => STATUS_INFO[s]?.label || s || "—";
export const statusTone = (s) => STATUS_INFO[s]?.tone || "grey";

/** Statuses an executor can pick from (approval happens through the review action). */
export const EXECUTOR_STATUSES = ["Not Started", "In Progress", "In Review", "Blocked", "Scheduled", "Posted", "Done"];

const dateFmt = new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short" });
const dateFmtYear = new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", year: "numeric" });

export function toISODate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseISODate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** "2026-10-22" -> "22 Oct" (adds the year when it is not this year). */
export function fmtShortDate(s) {
  const d = parseISODate(s);
  if (!d) return "—";
  return d.getFullYear() === new Date().getFullYear() ? dateFmt.format(d) : dateFmtYear.format(d);
}

/** Friendly relative day: "Today", "Tomorrow", "2 days late", "in 5 days". */
export function relativeDay(s, today = toISODate()) {
  const d = parseISODate(s);
  const t = parseISODate(today);
  if (!d || !t) return "";
  const diff = Math.round((d - t) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff < 0) return `${-diff} days late`;
  return `in ${diff} days`;
}

export const fmtTime = (t) => {
  if (!t) return "";
  const [hh, mm] = String(t).split(":");
  const h = Number(hh);
  return `${h % 12 || 12}:${mm} ${h < 12 ? "AM" : "PM"}`;
};

export const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

export const nullIfBlank = (v) => (v === "" || v === undefined ? null : v);
